import React, { FC, useState, useEffect, useRef } from "react";
import { Form, FormInstance, Input, Space, Typography, Button } from "antd";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { orgUnitCascaderFromValue } from "./create-proposal";
import {
  RelevantMatter,
  AccruedFee,
  MatterFilter,
  ExternalMatter,
  BaseAccruedFee,
} from "../../data-types";
import { FindRelevantMattersQuery, FindRelevantExternalMattersQuery } from "~/gql/graphql";
import { MatterTitleScreenTabKey, RelevantMatterTabs } from "./relevant-matter-tabs";
import { TitleInputState } from "./create-proposal";
import { ClarifyingQuestionsSection } from "./clarifying-questions-section";
import { StreamingSummarySection } from "./streaming-summary-section";
import styles from "./create-proposal.css";
import { useSearchParams } from "react-router-dom";
import { SearchFlowState } from "~/containers/create-proposal-container";

const {
  ["floating-input-container"]: floatingInputContainerClassName,
  ["floating-input"]: floatingInputClassName,
  ["floating-button"]: floatingButtonClassName,
  ["input-container"]: inputContainerClassName,
  ["title-hint"]: titleHintClassName,
  ["title-hint-text"]: titleHintTextClassName,
  ["main-content"]: mainContentClassName,
  ["warning-container"]: warningContainerClassName,
  ["workflow-guide-button"]: workflowGuideButtonClassName,
  spacer: spacerClassName,
} = styles;

interface StreamingState {
  sessionId: string | null;
  streamingQuestions: any[];
  totalQuestions: number | null;
  isComplete: boolean;
  error: string | null;
  streamingActive: boolean;
}

interface SummaryStreamingState {
  sessionId: string | null;
  chunks: string[];
  fullSummary: string | null;
  isComplete: boolean;
  error: string | null;
  streamingActive: boolean;
}

type MatterTitleScreenProps = {
  form: FormInstance;
  matterTitle: string;
  additionalSearchText: string;
  setAdditionalSearchText: (value: string) => void;
  selectedMatter: RelevantMatter | null;
  setSelectedMatter: (value: RelevantMatter | null) => void;
  selectedAccruedFees: AccruedFee | null;
  selectedExternalMatter: ExternalMatter | null;
  setSelectedExternalMatter: (value: ExternalMatter | null) => void;
  selectedExternalAccruedFees: BaseAccruedFee | null;
  templateMatters: FindRelevantMattersQuery | undefined;
  historicalMatters: FindRelevantMattersQuery | undefined;
  historicalComparisonLoading: boolean;
  externalMatters: FindRelevantExternalMattersQuery | undefined;
  templateMattersLoading: boolean;
  historicalMattersLoading: boolean;
  handleMatterTitleChange: (value: string) => void;
  activeTab: MatterTitleScreenTabKey;
  setActiveTab: (value: MatterTitleScreenTabKey) => void;
  matterFilter: MatterFilter;
  handleFilterChange: (value: MatterFilter) => void;
  uiState: TitleInputState;
  setUiState: (state: TitleInputState) => void;
  onAnswerSubmit: (questionId: string, answers: string[]) => void;
  onSkipQuestions: () => void;
  onSkipQuestion: (questionId: string) => void;
  clarifyingQuestionAnswers?: Record<string, string[]>;
  onStartOver?: (newTitle: string) => void;
  showClarifyingQuestionsEnabled?: boolean;
  // Streaming props
  streamingState?: StreamingState;
  // Summary streaming props
  summaryStreamingState?: SummaryStreamingState;
  searchFlowState?: SearchFlowState;
};

export const MatterTitleScreen: FC<MatterTitleScreenProps> = ({
  form,
  matterTitle,
  additionalSearchText,
  setAdditionalSearchText,
  selectedMatter,
  selectedAccruedFees,
  setSelectedMatter,
  selectedExternalMatter,
  setSelectedExternalMatter,
  selectedExternalAccruedFees,
  templateMatters,
  historicalMatters,
  historicalComparisonLoading,
  externalMatters,
  templateMattersLoading,
  historicalMattersLoading,
  handleMatterTitleChange,
  activeTab,
  setActiveTab,
  matterFilter,
  handleFilterChange,
  uiState,
  setUiState,
  onSkipQuestions,
  onSkipQuestion,
  onAnswerSubmit,
  clarifyingQuestionAnswers,
  onStartOver,
  showClarifyingQuestionsEnabled = true,
  // Streaming props
  streamingState,
  // Summary streaming props
  summaryStreamingState,
  searchFlowState,
}) => {
  const [inputValue, setInputValue] = useState(matterTitle || "");
  const [showClarifyingQuestions, setShowClarifyingQuestions] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [otherText, setOtherText] = useState<string>("");

  const [params] = useSearchParams();

  const title = params.get("title");

  if (title) {
    handleMatterTitleChange(title);
  }

  const stateUpdatedRef = useRef(false);

  useEffect(() => {
    if (matterTitle === "" && !title) {
      setInputValue("");
      form.setFieldValue("title", "");
    } else if (matterTitle || title) {
      setInputValue(title || matterTitle);
      form.setFieldValue("title", title || matterTitle);
    }
  }, [matterTitle, form, title]);

  // Handle title confirmations when navigating between screens
  useEffect(() => {
    if (stateUpdatedRef.current) {
      stateUpdatedRef.current = false;
      return;
    }
    if (matterTitle && uiState === TitleInputState.CENTERED) {
      stateUpdatedRef.current = true;
      setUiState(TitleInputState.TOP_WITH_ANIMATION);
    } else if (!matterTitle && uiState === TitleInputState.TOP_WITH_ANIMATION) {
      stateUpdatedRef.current = true;
      setUiState(TitleInputState.CENTERED);
    }
  }, [matterTitle, uiState, setUiState]);

  // Reset local question state when matterTitle is cleared or streaming state is reset
  useEffect(() => {
    const hasNoTitle = !matterTitle;
    const hasNoActiveStreaming =
      !streamingState?.streamingActive &&
      (!streamingState?.streamingQuestions || streamingState.streamingQuestions.length === 0);

    if (hasNoTitle || hasNoActiveStreaming) {
      setCurrentQuestionIndex(0);
      setSelectedAnswers([]);
      setOtherText("");
    }
  }, [matterTitle, streamingState?.streamingActive, streamingState?.streamingQuestions]);

  // Show clarifying questions when title is confirmed and questions are available
  useEffect(() => {
    // Show questions if:
    // 1. The setting is enabled AND
    // 2. We're in the questions_answering state AND
    // 3. (Streaming is active OR we have questions available)
    const shouldShowQuestions = Boolean(
      showClarifyingQuestionsEnabled &&
        matterTitle &&
        searchFlowState === "questions_answering" &&
        (streamingState?.streamingActive ||
          (streamingState?.streamingQuestions && streamingState.streamingQuestions.length > 0))
    );

    setShowClarifyingQuestions(shouldShowQuestions);
  }, [matterTitle, streamingState, searchFlowState, showClarifyingQuestionsEnabled]);

  // Restore answer state when question changes
  useEffect(() => {
    const questions = streamingState?.streamingQuestions || [];
    const currentQuestion = questions[currentQuestionIndex];

    if (currentQuestion && clarifyingQuestionAnswers) {
      const existingAnswers = clarifyingQuestionAnswers[currentQuestion.id] || [];

      // Set selected answers directly from stored answers
      setSelectedAnswers(existingAnswers);

      // Check if "other" is selected and set other text accordingly
      if (existingAnswers.includes("other")) {
        // Find the "other" text from the stored answers
        const otherAnswers = existingAnswers.filter((answer: any) => {
          // Check if this answer is not one of the predefined options
          return (
            !currentQuestion.options?.some((option: any) => option.value === answer) &&
            answer !== "other"
          );
        });
        setOtherText(otherAnswers.join(", ") || "");
      } else {
        setOtherText("");
      }
    } else {
      // No question or answers available, reset to empty
      setSelectedAnswers([]);
      setOtherText("");
    }
  }, [
    currentQuestionIndex,
    clarifyingQuestionAnswers,
    // Only track the current question specifically, not the entire array
    streamingState?.streamingQuestions?.[currentQuestionIndex]?.id,
  ]);

  const isInputCentered = () => uiState === TitleInputState.CENTERED;

  const shouldDisableTransitions = () => uiState === TitleInputState.TOP_WITHOUT_ANIMATION;

  const handleSelectedMatterChange = (matter: RelevantMatter | null) => {
    setSelectedMatter(matter);
    if (matter) {
      if (matter.organisationalUnit) {
        form.setFieldValue("practiceAreaId", orgUnitCascaderFromValue(matter.organisationalUnit));
      }
      if (matter.template) {
        form.setFieldValue("templateId", matter.id);
      } else {
        form.setFieldValue("historicalMatterId", matter.id);
      }
    } else {
      form.setFieldsValue({
        practiceAreaId: null,
        pricingStructure: null,
        copyScope: false,
        copyResourcing: false,
        copyPricing: false,
        templateId: null,
        historicalMatterId: null,
      });
    }
  };

  const confirmTitle = (value: string) => {
    if (value.trim()) {
      handleMatterTitleChange(value);
      stateUpdatedRef.current = true;
      setUiState(TitleInputState.TOP_WITH_ANIMATION);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (isInputCentered()) {
        confirmTitle((e.target as HTMLInputElement).value);
      } else if (inputValue.trim() !== matterTitle.trim()) {
        handleStartOver();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    form.setFieldValue("title", value);
  };

  const handleAnswerChange = (values: string[]) => {
    setSelectedAnswers(values);
  };

  const handleNextQuestion = (answerValues?: string[]) => {
    const questions = streamingState?.streamingQuestions || [];
    const currentQuestion = questions[currentQuestionIndex];

    if (!currentQuestion) return;

    // Use the passed answer values if provided, otherwise fall back to component state
    const finalAnswers = answerValues || selectedAnswers;

    // If "other" is selected, include both the "other" value and the other text in the answers
    const answersToSubmit =
      finalAnswers.includes("other") && otherText.trim()
        ? [...finalAnswers, otherText.trim()]
        : finalAnswers;

    onAnswerSubmit(currentQuestion.id, answersToSubmit);

    // Move to next question or finish
    advanceToNextQuestionOrFinish();
  };

  const handleSkipQuestion = () => {
    // Skip the current question without storing any answer
    const questions = streamingState?.streamingQuestions || [];
    const currentQuestion = questions[currentQuestionIndex];

    if (currentQuestion) {
      onSkipQuestion(currentQuestion.id);
    }

    // Advance to the next question
    advanceToNextQuestionOrFinish();
  };

  const advanceToNextQuestionOrFinish = () => {
    const questions = streamingState?.streamingQuestions || [];
    const currentQuestion = questions[currentQuestionIndex];

    // Check if current question is the final question (free-form text with no options, just allowOther)
    const isFinalQuestion =
      currentQuestion &&
      (!currentQuestion.options || currentQuestion.options.length === 0) &&
      currentQuestion.allowOther;

    if (isFinalQuestion) {
      // Final question processed, hide questions and show relevant matters
      setShowClarifyingQuestions(false);
    } else {
      // Move to next question (will show loading if not available yet)
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleSkipQuestions = () => {
    onSkipQuestions();
    setShowClarifyingQuestions(false);
  };

  const handleGoBack = () => {
    if (currentQuestionIndex > 0) {
      // Simply move to previous question - don't clear any answers
      // The useEffect will handle restoring the answer state for the previous question
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleStartOver = () => {
    // Reset local state for question UI
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setOtherText("");

    // Call handler from parent to restart streaming
    onStartOver?.(inputValue);
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        className={floatingInputContainerClassName}
        style={{
          transform: isInputCentered()
            ? "translate(calc(45vw - 50%), 35vh) scale(1.1)"
            : "translate(24px, -8px) scale(1)",
          transition: shouldDisableTransitions()
            ? "none"
            : "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Form.Item
          name="title"
          rules={[
            {
              required: true,
              message: "Please enter a title for your proposal",
            },
          ]}
          style={{ margin: 0 }}
        >
          <div className={inputContainerClassName}>
            <Input
              placeholder="Enter the title of your proposal"
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              className={floatingInputClassName}
              style={{
                fontSize: isInputCentered() ? "16px" : "14px",
                transition: shouldDisableTransitions()
                  ? "none"
                  : "font-size 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                padding: isInputCentered() ? "10px 16px" : undefined,
              }}
              value={inputValue}
            />
            {isInputCentered() ? (
              <Button
                type="primary"
                onClick={() => confirmTitle(inputValue)}
                className={floatingButtonClassName}
                style={{
                  fontSize: isInputCentered() ? "16px" : "14px",
                  transition: shouldDisableTransitions()
                    ? "none"
                    : "font-size 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  padding: isInputCentered() ? "10px 20px" : undefined,
                }}
              >
                Confirm
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleStartOver}
                className={floatingButtonClassName}
                style={{
                  fontSize: isInputCentered() ? "16px" : "14px",
                  transition: shouldDisableTransitions()
                    ? "none"
                    : "font-size 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  padding: isInputCentered() ? "10px 20px" : undefined,
                }}
                disabled={inputValue.trim() === matterTitle.trim()}
              >
                Start over
              </Button>
            )}
          </div>
        </Form.Item>
        {isInputCentered() && (
          <div className={titleHintClassName}>
            <Typography.Text type="secondary">
              <div className={titleHintTextClassName}>
                Enter a title to see relevant proposal templates and historical matters. You can
                create a proposal from scratch, choose a <b>template</b>, or copy the scope and
                pricing details from a <b>historical matter</b>.
                <div style={{ marginTop: "8px", textAlign: "center" }}>
                  <a
                    href={`https://${window.location.host}/create?userpilot=ZXhwZXJpZW5jZTpyWlUyWDRxekdC`}
                    className={workflowGuideButtonClassName}
                  >
                    <QuestionCircleOutlined style={{ marginRight: "6px", fontSize: "12px" }} />
                    See the new workflow explained ✨
                  </a>
                </div>
              </div>
            </Typography.Text>
          </div>
        )}
      </div>

      <Form form={form} layout="vertical">
        <div className={spacerClassName} />

        {!isInputCentered() &&
          summaryStreamingState &&
          searchFlowState &&
          (searchFlowState === "generating_summary" ||
            searchFlowState === "waiting_for_search_results_with_summary") && (
            <StreamingSummarySection
              summaryStreamingState={summaryStreamingState}
              searchFlowState={searchFlowState}
            />
          )}

        <div
          className={mainContentClassName}
          style={{
            opacity: isInputCentered() ? 0 : 1,
            transform: `translateY(${isInputCentered() ? "20px" : "0"})`,
            transition: shouldDisableTransitions()
              ? "none"
              : "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            transitionDelay: isInputCentered() ? "0s" : "0.4s",
            visibility: isInputCentered() ? "hidden" : "visible",
            pointerEvents: isInputCentered() ? "none" : "auto",
          }}
        >
          {/* {isMatterTitleDescriptive === false && (
            <Space align="start" className={warningContainerClassName}>
              <InfoCircleOutlined />
              <div>
                For more relevant suggestions, consider using a more descriptive title for your
                proposal (or enter more search terms in the search bar below).
              </div>
            </Space>
          )} */}

          {matterTitle && (
            <>
              {showClarifyingQuestions && (
                <ClarifyingQuestionsSection
                  currentQuestionIndex={currentQuestionIndex}
                  selectedAnswers={selectedAnswers}
                  otherText={otherText}
                  onAnswerChange={handleAnswerChange}
                  onOtherTextChange={(value: string) => setOtherText(value)}
                  onSkipQuestions={handleSkipQuestions}
                  onGoBack={handleGoBack}
                  // Streaming props
                  streamingState={streamingState}
                  onAutoAdvance={handleNextQuestion}
                  onSkipQuestion={handleSkipQuestion}
                  matterTitle={matterTitle}
                  onPracticeAreaSelect={(practiceAreaId, parentId) => {
                    const cascaderValue = parentId ? [parentId, practiceAreaId] : [practiceAreaId];
                    form.setFieldValue("practiceAreaId", cascaderValue);
                    handleFilterChange({
                      ...matterFilter,
                      orgUnit: practiceAreaId,
                    });
                  }}
                  onPracticeAreaClear={() => {
                    form.setFieldValue("practiceAreaId", null);
                    handleFilterChange({
                      ...matterFilter,
                      orgUnit: null,
                    });
                  }}
                />
              )}

              {!showClarifyingQuestions &&
                searchFlowState !== "generating_summary" &&
                searchFlowState !== "waiting_for_search_results_with_summary" &&
                searchFlowState !== "questions_answering" && (
                  <RelevantMatterTabs
                    createMode={true}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    selectedMatter={selectedMatter}
                    handleSelectedMatterChange={handleSelectedMatterChange}
                    selectedAccruedFees={selectedAccruedFees}
                    selectedExternalMatter={selectedExternalMatter}
                    selectedExternalAccruedFees={selectedExternalAccruedFees}
                    handleSelectedExternalMatterChange={setSelectedExternalMatter}
                    templateMatters={templateMatters}
                    historicalMatters={historicalMatters}
                    historicalComparisonLoading={historicalComparisonLoading}
                    externalMatters={externalMatters}
                    additionalSearchText={additionalSearchText}
                    setAdditionalSearchText={setAdditionalSearchText}
                    templateMattersLoading={templateMattersLoading}
                    historicalMattersLoading={historicalMattersLoading}
                    maxHeight="65vh"
                    matterFilter={matterFilter}
                    handleFilterChange={handleFilterChange}
                    allowChangeCurrency={true}
                  />
                )}
            </>
          )}
        </div>
      </Form>
    </div>
  );
};
